import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
 Store,
 User,
 Mail,
 Lock,
 ArrowLeft,
 ArrowRight,
 AlertCircle,
 Check,
 Sparkles,
 ShieldCheck,
 Crown,
 Plus,
 Minus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { LanguageProvider } from '../context/LanguageContext';
import { GoldBellMark } from '../components/NoFeeLogo';
import StripeRedirectOverlay from '../components/StripeRedirectOverlay';
import {
 StripeWordmark,
 PaymentNetworkRow,
} from '../components/PaymentLogos';
import DashboardPreview from '../components/DashboardPreview';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_INFO = {
 ordering: { label: 'NoFee Ordering', price: '$299/month', trial: '45-day free trial' },
 pos: { label: 'NoFee POS Lite', price: '$399/mo for 6 months, then $449/mo', trial: '45-day free trial' },
 bundle: { label: 'NoFee Bundle', price: '$599/mo for 6 months, then $649/mo', trial: '45-day free trial · Save $99/mo' },
 default: { label: 'NoFee — Founding Partner', price: '$299/month', trial: '45-day free trial' },
};

const FOUNDING_BENEFITS = [
 '0% commission on every order — forever',
 'Restaurants keep 100% of profits',
 'Direct Stripe payouts to your own accounts',
 'Setup in under 5 minutes',
 'Premium digital menu included',
 'Cancel anytime · no contracts',
];

const FAQS = [
 { q: 'Can I cancel anytime?', a: 'Yes. Cancel from your dashboard in one click. No contracts, no cancellation fees.' },
 { q: 'When do payouts arrive?', a: 'Customer orders pay you directly through Stripe. Payments go directly to your own Stripe account. NoFee never holds your money. Payouts are sent according to your Stripe payout schedule.' },
 { q: 'Is this secure?', a: 'Checkout is processed by Stripe with PCI-DSS Level 1 compliance and 256-bit TLS encryption. We never see or store your card.' },
 { q: 'When will I be charged?', a: 'Not today. Your 45-day free trial starts the moment you complete checkout. Cancel before day 45 and pay nothing.' },
];

const FaqAccordion = () => {
 const [open, setOpen] = useState(null);
 return (
 <div className="rounded-[20px] border border-stone-300/40 bg-white/60 backdrop-blur-md overflow-hidden" data-testid="start-faq">
 {FAQS.map((f, i) => {
 const isOpen = open === i;
 return (
 <div key={i} className={i!== FAQS.length - 1? 'border-b border-stone-200/60': ''}>
 <button
 type="button"
 onClick={() => setOpen(isOpen? null: i)}
 data-testid={`start-faq-q-${i}`}
 className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/40 transition-colors"
 aria-expanded={isOpen}
 >
 <span className="text-[14px] font-bold text-neutral-800">{f.q}</span>
 {isOpen? <Minus className="w-4 h-4 text-neutral-500 flex-shrink-0" />: <Plus className="w-4 h-4 text-neutral-500 flex-shrink-0" />}
 </button>
 {isOpen && <p className="px-5 pb-4 -mt-1 text-[13.5px] text-neutral-600 leading-[1.6]">{f.a}</p>}
 </div>
 );
 })}
 </div>
 );
};

// Session storage key for in-progress signup form. Cleared on successful
// register submit so subsequent visitors don't see another user's draft.
const FORM_STORAGE_KEY = 'nofee_signup_form_draft';

const StartSignupPage = () => {
 const { register } = useAuth();
 const navigate = useNavigate();
 const [params] = useSearchParams();
 const planKey = (params.get('plan') || '').toLowerCase();
 const plan = PLAN_INFO[planKey] || PLAN_INFO.default;
 const refCode = (params.get('ref') || '').toUpperCase();

 // Rehydrate the form from sessionStorage so clicking Back → previous page
 // → coming back never wipes what the owner already typed. We use session
 // storage (not localStorage) so the draft auto-clears when they close the
 // tab — never persists across separate restaurant owners.
 const [form, setForm] = useState(() => {
 if (typeof window === 'undefined') return { restaurant_name: '', name: '', email: '', password: '' };
 try {
 const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
 if (raw) {
 const parsed = JSON.parse(raw);
 return {
 restaurant_name: parsed.restaurant_name || '',
 name: parsed.name || '',
 email: parsed.email || '',
 // Never restore passwords from storage for security.
 password: '',
 };
 }
 } catch { /* ignore parse errors */ }
 return { restaurant_name: '', name: '', email: '', password: '' };
 });
 const [loading, setLoading] = useState(false);
 const [redirecting, setRedirecting] = useState(false);
 const [error, setError] = useState('');

 // Auto-persist on every change. Excludes password.
 useEffect(() => {
 try {
 const { password,...rest } = form; // eslint-disable-line no-unused-vars
 sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(rest));
 } catch { /* ignore quota errors */ }
 }, [form]);

 // Back button — uses browser history when there is one, otherwise falls
 // back to the marketing benefits page.
 const handleBack = () => {
 if (window.history.length > 1) {
 navigate(-1);
 } else {
 navigate('/for-restaurants');
 }
 };

 useEffect(() => {
 const el = document.querySelector('[data-testid="start-restaurant-name"]');
 if (el) el.focus({ preventScroll: true });
 }, []);

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError('');
 if (!form.restaurant_name.trim() ||!form.name.trim() ||!form.email.trim() ||!form.password) {
 setError('Please complete every field to continue.');
 return;
 }
 if (form.password.length < 6) {
 setError('Password must be at least 6 characters.');
 return;
 }
 setLoading(true);
 try {
 const payload = {
 restaurant_name: form.restaurant_name.trim(),
 name: form.name.trim(),
 email: form.email.trim().toLowerCase(),
 password: form.password,
 phone: '',
 address: '',
 cuisine_type: '',
 how_heard: 'for-restaurants',
 plan: planKey || 'ordering',
 };
 if (refCode) payload.referral_code = refCode;
 await register(payload);
 // Successful register → clear the draft so the next visitor on this
 // tab gets a fresh form.
 try { sessionStorage.removeItem(FORM_STORAGE_KEY); } catch { /* ignore */ }

 // CRITICAL: do NOT trigger Stripe checkout here. The user just created
 // their account — payment must come ONLY after they finish onboarding
 // (business hours, menu, Stripe Connect, etc.). The /onboarding hub
 // owns the gating: it advances the user step-by-step and only exposes
 // a "Complete subscription" CTA once everything else is ready.
 setRedirecting(true);
 setTimeout(() => navigate('/onboarding'), 400);
 } catch (err) {
 setRedirecting(false);
 // FastAPI/Pydantic 422 returns `detail` as an array of validation errors
 // (e.g. `[{type, loc, msg, ctx,...}]`). Rendering that array directly
 // through React crashes the page with "Something went wrong" because
 // React can't print objects. Normalize to a clean string.
 const detail = err?.response?.data?.detail;
 let msg = 'Could not create your account. Please try again.';
 if (typeof detail === 'string') {
 msg = detail;
 } else if (Array.isArray(detail) && detail[0]?.msg) {
 // Strip Pydantic's "Value error, " prefix so users see only the
 // human-readable message.
 msg = String(detail[0].msg).replace(/^Value error,\s*/i, '');
 }
 setError(msg);
 } finally {
 setLoading(false);
 }
 };

 const inputClass =
 'w-full pl-12 pr-4 h-[54px] rounded-2xl bg-white border border-stone-200/80 text-neutral-900 placeholder:text-neutral-400 focus:border-gold-500 focus:ring-4 focus:ring-gold-500/10 focus:bg-white outline-none transition-all duration-200 text-[15px] shadow-[0_1px_2px_0_rgba(0,0,0,0.02)]';

 return (
 <LanguageProvider>
 <div
 className="min-h-screen w-full relative overflow-hidden"
 data-testid="start-signup-page"
 style={{
 background:
 'radial-gradient(140% 90% at 50% -10%, #faf6ee 0%, #f7f1e4 30%, #f3ecdb 65%, #efe6cf 100%)',
 }}
 >
 <SEO
 title={`Start free trial · ${plan.label}`}
 description="Join NoFee. 0% commission food ordering with direct Stripe payouts. 45-day free trial — cancel anytime."
 path="/start"
 noindex
 />

 <div
 className="pointer-events-none absolute inset-0 opacity-70"
 style={{
 background:
 'radial-gradient(55% 50% at 90% 12%, rgba(212,175,55,0.14) 0%, transparent 60%), radial-gradient(45% 40% at 8% 88%, rgba(212,175,55,0.10) 0%, transparent 60%)',
 }}
 />

 {redirecting && <StripeRedirectOverlay message="Activating your restaurant — connecting to Stripe…" />}

 {/* Consistent Navbar — same hamburger menu used across the site.
 `forceSolid` keeps the black background from first paint (no
 translucent state) for premium contrast on the signup flow. */}
 <Navbar forceSolid />

 <main className="relative z-10 px-5 sm:px-8 lg:px-12 pb-20 pt-24 sm:pt-28">
 <div className="w-full max-w-[1240px] mx-auto">
 {/* Back navigation — always visible above the form. Uses browser
 history when there is one, otherwise falls back to the
 marketing benefits page. */}
 <button
 type="button"
 onClick={handleBack}
 data-testid="start-back-btn"
 className="inline-flex items-center gap-2 mb-6 lg:mb-8 px-4 py-2 rounded-full border border-neutral-300 bg-white/70 backdrop-blur-sm text-neutral-700 hover:text-neutral-900 hover:border-neutral-400 hover:bg-white transition-all text-[13.5px] font-semibold"
 >
 <ArrowLeft className="w-4 h-4" />
 Back
 </button>
 </div>
 <div className="w-full max-w-[1240px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14">

 {/* LEFT — Emotional / Value */}
 <section className="order-2 lg:order-1 max-w-[600px]">
 <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold-500/40 bg-gradient-to-r from-gold-500/[0.06] to-amber-500/[0.06] backdrop-blur-sm mb-6">
 <Crown className="w-3.5 h-3.5 text-gold-600" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-gold-800 font-bold">
 Founding Restaurant Partner
 </span>
 </div>

 <h1
 className="text-[44px] sm:text-[56px] lg:text-[64px] font-black tracking-[-0.025em] leading-[1.0] text-neutral-900"
 data-testid="start-headline"
 >
 45 Days
 <br />
 <span className="bg-gradient-to-r from-amber-700 via-gold-600 to-amber-800 bg-clip-text text-transparent">
 Free.
 </span>
 </h1>

 <p className="mt-5 text-[18px] sm:text-[19px] text-neutral-700 leading-[1.55] max-w-[520px] font-medium">
 Keep more profit every month with <span className="text-neutral-900 font-bold">0% commission</span>.
 Built for restaurants who want to keep more of every order.
 </p>

 <ul className="mt-9 space-y-3" data-testid="start-trust-list">
 {FOUNDING_BENEFITS.map((b) => (
 <li key={b} className="flex items-start gap-3 text-[15px] text-neutral-700">
 <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/35 flex items-center justify-center">
 <Check className="w-3 h-3 text-emerald-700" strokeWidth={3} />
 </span>
 <span>{b}</span>
 </li>
 ))}
 </ul>

 <div className="mt-8 inline-flex items-center gap-2 text-[12.5px] text-neutral-600">
 <Sparkles className="w-3.5 h-3.5 text-gold-600" />
 <span><span className="font-bold text-neutral-800">Limited founding partner access</span> — priority support included.</span>
 </div>

 <div className="mt-10 hidden sm:block">
 <DashboardPreview caption="Your dashboard — once activated" />
 </div>
 </section>

 {/* RIGHT — Action card */}
 <section className="order-1 lg:order-2 w-full">
 <div
 className="relative bg-white/85 backdrop-blur-xl rounded-[28px] border border-white/70 p-7 sm:p-9 shadow-[0_40px_100px_-25px_rgba(120,80,20,0.22),0_10px_30px_-10px_rgba(0,0,0,0.08)]"
 data-testid="start-card"
 >
 {/* Plan header */}
 <div className="flex items-start justify-between gap-4 mb-7">
 <div>
 <p className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">
 Your selected plan
 </p>
 <p className="mt-1 text-[15px] text-neutral-700 font-bold">{plan.label}</p>
 <p className="mt-2 text-[32px] font-black tracking-tight text-neutral-900 leading-none">
 {plan.price.split(',')[0]}
 </p>
 </div>
 <div className="text-right">
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-50">
 <span className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-800 font-bold">
 {plan.trial.split('·')[0].trim()}
 </span>
 </div>
 <p className="mt-2 text-[11.5px] text-neutral-500">No charge today</p>
 </div>
 </div>

 <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="start-signup-form">
 {error && (
 <div className="p-3 bg-red-50 text-red-700 text-[13px] rounded-2xl border border-red-200 flex items-start gap-2" data-testid="start-error">
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{error}</span>
 </div>
 )}

 <div className="relative">
 <Store className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="text"
 required
 value={form.restaurant_name}
 onChange={(e) => setForm({...form, restaurant_name: e.target.value })}
 placeholder="Restaurant name"
 className={inputClass}
 data-testid="start-restaurant-name"
 autoComplete="organization"
 />
 </div>

 <div className="relative">
 <User className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="text"
 required
 value={form.name}
 onChange={(e) => setForm({...form, name: e.target.value })}
 placeholder="Your full name"
 className={inputClass}
 data-testid="start-full-name"
 autoComplete="name"
 />
 </div>

 <div className="relative">
 <Mail className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="email"
 required
 value={form.email}
 onChange={(e) => setForm({...form, email: e.target.value })}
 placeholder="Email address"
 className={inputClass}
 data-testid="start-email"
 autoComplete="email"
 />
 </div>

 <div className="relative">
 <Lock className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="password"
 required
 minLength={6}
 value={form.password}
 onChange={(e) => setForm({...form, password: e.target.value })}
 placeholder="Create a password (6+ characters)"
 className={inputClass}
 data-testid="start-password"
 autoComplete="new-password"
 />
 </div>

 {/* Premium CTA */}
 <button
 type="submit"
 disabled={loading || redirecting}
 data-testid="start-submit"
 className="group relative w-full mt-2 h-[62px] inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-neutral-900 to-black text-white font-extrabold tracking-[0.01em] text-[16px] transition-all duration-300 shadow-[0_14px_36px_-10px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_20px_50px_-14px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.16)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 overflow-hidden"
 >
 <span
 className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
 style={{
 background:
 'linear-gradient(120deg, transparent 25%, rgba(212,175,55,0.22) 50%, transparent 75%)',
 }}
 />
 <span className="relative inline-flex items-center gap-2">
 <Crown className="w-4 h-4 text-gold-400" />
 {loading? 'Creating your account…': redirecting? 'Activating…': 'Activate My Restaurant'}
 </span>
 {!loading &&!redirecting && (
 <ArrowRight className="relative w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
 )}
 </button>

 <div className="mt-1 flex items-center justify-center gap-2">
 <ShieldCheck className="w-3.5 h-3.5 text-neutral-500" />
 <p className="text-[12px] text-neutral-600">
 Trusted secure checkout powered by{' '}
 <span className="font-bold text-neutral-800">Stripe</span>
 </p>
 </div>

 {/* Compliance strip */}
 <div className="pt-4 mt-1 border-t border-neutral-200/70 grid grid-cols-3 gap-2 text-center">
 {[['PCI', 'Level 1'], ['256-bit', 'TLS encrypted'], ['SSL', 'Secured']].map(([k, v]) => (
 <div key={k}>
 <p className="text-[11px] font-black text-neutral-800 tracking-tight">{k}</p>
 <p className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold mt-0.5">{v}</p>
 </div>
 ))}
 </div>

 {/* Payment networks */}
 <div className="pt-4 mt-1 border-t border-neutral-200/70">
 <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3 text-center">
 Accepted payment methods
 </p>
 <PaymentNetworkRow className="justify-center" logoClass="h-5 sm:h-6 w-auto opacity-90" />
 </div>

 <p className="text-center text-[11px] text-neutral-500 pt-3 leading-relaxed">
 By continuing you agree to the{' '}
 <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-700 font-semibold hover:text-gold-700 hover:underline transition-colors">Terms</a>{' '}
 &{' '}
 <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-700 font-semibold hover:text-gold-700 hover:underline transition-colors">Privacy</a>.
 </p>
 </form>
 </div>

 {/* FAQ */}
 <div className="mt-8">
 <p className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3 px-1">
 Quick answers
 </p>
 <FaqAccordion />
 </div>

 {/* Bottom partners */}
 <div className="mt-8 flex items-center justify-center gap-6 sm:gap-8 opacity-90">
 <StripeWordmark className="h-6 w-auto" />
 </div>

 <p className="text-center text-[13.5px] text-neutral-500 mt-6">
 Already have an account?{' '}
 <Link
 to="/login?role=restaurant"
 className="text-neutral-800 font-bold hover:text-gold-700 transition-colors"
 data-testid="start-login-link"
 >
 Sign in
 </Link>
 </p>
 </section>
 </div>
 </main>
 </div>
 </LanguageProvider>
 );
};

export default StartSignupPage;
